import AdminTable from '../../../components/admin/AdminTable';
import { OperadorPinDialog, useOperadorGrant } from '../../../components/pos/OperadorPinDialog';
// src/pages/Admin/pos/ReservasCalendariaPage.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Calendar, dateFnsLocalizer, Views, View } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import {
    format,
    parse,
    startOfWeek,
    getDay,
    startOfMonth,
    endOfMonth,
    endOfWeek,
    startOfDay,
    endOfDay,
    addDays,
} from 'date-fns';
import { es } from 'date-fns/locale/es';
import { Link, useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    ChevronRight,
    Calendar as CalendarIcon,
    List,
    Plus,
    X,
    Building2,
    Search,
    Clock,
    CheckCircle2,
    AlertCircle,
    Eye,
    Edit,
    FileText,
    Sparkles,
    ShieldCheck,
    Save,
    MapPin,
    ArrowUpRight,
    Home,
    Layers,
    Info,
    RefreshCw
} from 'lucide-react';

// Servicios y tipos
import { fetchCitas, patchCita, Cita } from '../../../services/citasService';
import { fetchSucursales, Sucursal } from '../../../services/pos/sucursalService';
import { useWebSocketCitas } from '../../../hooks/useWebSocket';

// Estilos base de react-big-calendar
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

// -------------------------------------------------------------------
// CONFIGURACIÓN DE LOCALIZACIÓN EN ESPAÑOL
// -------------------------------------------------------------------
const locales = { es };

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }), // Lunes como primer día
    getDay,
    locales,
});

// Interfaz para los eventos del calendario
interface CalendarEvent {
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    resource?: Cita;
}

// Componente de calendario con Drag & Drop
const DragAndDropCalendar = withDragAndDrop(Calendar);

// -------------------------------------------------------------------
// PALETA DE COLORES PASANTÍAS (CORPORATIVA)
// -------------------------------------------------------------------
const colors = {
    brand: '#61257b',         // Púrpura corporativo primario
    brandDark: '#3d1250',     // Púrpura oscuro
    brandLight: '#8b3fa8',    // Púrpura suave
    accent1: '#c1348b',       // Magenta / Acento principal
    accent2: '#ea8406',       // Naranja cálido
    accent3: '#ffd97c',       // Amarillo dorado
    primary: '#61257b',
    primaryDark: '#3d1250',
    secondary: '#c1348b',
    secondaryLight: '#ea8406',
    accent: '#ffd97c',
    accentDark: '#ea8406',
    slate: '#0f172a',
    muted: '#64748b',
    border: '#f1f5f9',
};

// -------------------------------------------------------------------
// ESTILOS ENCAPSULADOS
// -------------------------------------------------------------------
const calendarCss = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');

.rc-page {
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

.custom-scroll::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}
.custom-scroll::-webkit-scrollbar-track {
    background: transparent;
}
.custom-scroll::-webkit-scrollbar-thumb {
    background-color: rgba(97, 37, 123, 0.25);
    border-radius: 20px;
}
.custom-scroll::-webkit-scrollbar-thumb:hover {
    background-color: rgba(97, 37, 123, 0.5);
}

/* React Big Calendar customization */
.rbc-calendar {
    font-family: inherit;
    background: #fff;
}
.rbc-header {
    padding: 12px 6px;
    font-weight: 800;
    font-size: 0.75rem;
    color: #334155;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 2px solid #e2e8f0;
}
.rbc-month-view {
    border: 2px solid #e2e8f0;
    border-radius: 18px;
    overflow: hidden;
    background: #ffffff;
    box-shadow: 0 4px 24px rgba(0,0,0,0.06);
}
.rbc-day-bg + .rbc-day-bg {
    border-left: 1.5px solid #e8eef4;
}
.rbc-month-row + .rbc-month-row {
    border-top: 1.5px solid #e8eef4;
}
.rbc-month-row {
    min-height: 110px;
}
.rbc-row-content {
    z-index: 2;
}
.rbc-today {
    background: linear-gradient(180deg, #fbf7fc 0%, #f7eff9 100%) !important;
    position: relative;
}
.rbc-today .rbc-date-cell a {
    color: ${colors.brand};
    font-weight: 900;
}
.rbc-off-range-bg {
    background-color: #f9fafb;
    opacity: 0.7;
}
.rbc-date-cell {
    padding: 6px 10px;
    font-weight: 700;
    font-size: 0.85rem;
    color: #1e293b;
}
.rbc-off-range .rbc-button-link {
    color: #cbd5e1;
}
.rbc-now .rbc-button-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${colors.brand};
    color: #ffffff;
    font-weight: 900;
    box-shadow: 0 2px 8px rgba(97,37,123,0.35);
}
.rbc-event {
    background: transparent;
    padding: 0 !important;
    margin: 1px 0 !important;
    border: none;
    outline: none;
}
.rbc-event:focus {
    outline: none;
}
.rbc-event-content {
    font-size: 0.78rem;
}
.rbc-time-view {
    border: 2px solid #e2e8f0;
    border-radius: 18px;
    overflow: hidden;
    background: #ffffff;
    box-shadow: 0 4px 24px rgba(0,0,0,0.06);
}
.rbc-time-header {
    border-bottom: 2px solid #e2e8f0;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}
.rbc-time-header-cell.rbc-today {
    background: linear-gradient(180deg, #fdf2f8 0%, #fce7f3 100%) !important;
    color: ${colors.brand};
}
.rbc-time-content {
    border-top: 2px solid #e2e8f0;
}
.rbc-timeslot-group {
    border-bottom: 1px solid #e8eef4;
    min-height: 46px;
}
.rbc-time-slot {
    font-size: 0.72rem;
    color: #94a3b8;
    font-weight: 600;
}
.rbc-label {
    padding: 0 8px;
    color: #94a3b8;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.03em;
}
.rbc-day-slot .rbc-time-slot {
    border-top: 1px dashed #f0f4f8;
}
.rbc-current-time-indicator {
    background-color: ${colors.accent1};
    height: 2px;
    box-shadow: 0 0 6px rgba(193,52,139,0.5);
}
.rbc-agenda-view table.rbc-agenda-table {
    border: none;
    border-radius: 14px;
    overflow: hidden;
}
.rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
    padding: 10px 14px;
    border-top: 1px solid #f1f5f9;
    font-size: 0.82rem;
}
.rbc-agenda-view table.rbc-agenda-table thead > tr > th {
    background: #f8fafc;
    padding: 8px 14px;
    font-size: 0.72rem;
    font-weight: 800;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border-bottom: 1.5px solid #e2e8f0;
}
.rbc-agenda-empty {
    padding: 40px;
    text-align: center;
    color: #94a3b8;
    font-size: 0.9rem;
}
.rbc-show-more {
    color: ${colors.brand};
    font-weight: 800;
    font-size: 0.72rem;
    padding: 2px 8px;
    background: rgba(97,37,123,0.08);
    border-radius: 6px;
    margin-top: 2px;
    display: inline-block;
    cursor: pointer;
    transition: all 0.15s ease;
}
.rbc-show-more:hover {
    background: ${colors.brand};
    color: #ffffff;
    transform: translateY(-1px);
}
.rbc-overlay {
    z-index: 1000 !important;
    background: #ffffff !important;
    border-radius: 18px !important;
    box-shadow: 0 20px 48px rgba(0,0,0,0.18), 0 0 0 1.5px rgba(97,37,123,0.15) !important;
    border: 1px solid #e2e8f0 !important;
    padding: 14px !important;
    max-height: 400px !important;
    overflow-y: auto !important;
    min-width: 260px !important;
    font-family: inherit !important;
}
.rbc-overlay-header {
    font-weight: 900 !important;
    color: ${colors.brand} !important;
    padding-bottom: 8px !important;
    border-bottom: 1.5px solid #f1f5f9 !important;
    margin-bottom: 10px !important;
    text-transform: capitalize !important;
    font-size: 0.88rem !important;
}
.rbc-overlay .rbc-event {
    margin-bottom: 5px !important;
}

@keyframes rcFadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
}
.rc-fade {
    animation: rcFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.rc-pulse {
    animation: rcPulse 2s infinite;
}
@keyframes rcPulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.85; }
}
`;

// Helper para combinar fecha y hora
const combineDateAndTime = (dateStr: string, timeStr?: string | null): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (timeStr) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        date.setHours(hours, minutes, 0, 0);
    }
    return date;
};

// -------------------------------------------------------------------
// COMPONENTE PRINCIPAL
// -------------------------------------------------------------------
const ReservasCalendarioPage: React.FC = () => {
    const navigate = useNavigate();

    // Estados de datos
    const [pinSesionId, setPinSesionId] = useState<number | null>(null);
    const operador = useOperadorGrant(pinSesionId);
    useEffect(() => { if (pinSesionId && !operador.token) operador.openDialog(); }, [pinSesionId, operador.token, operador.openDialog]);
    const requireGrant = (cita: Cita) => {
        if (!cita.sesion_pos) { alert('La reserva no tiene una caja asociada. Ábrela desde el POS.'); return false; }
        if (pinSesionId !== cita.sesion_pos || !operador.token) {
            setPinSesionId(cita.sesion_pos);
            if (pinSesionId === cita.sesion_pos) operador.openDialog();
            return false;
        }
        return true;
    };
    const [citas, setCitas] = useState<Cita[]>([]);
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [selectedSucursalId, setSelectedSucursalId] = useState<number | 'todas'>('todas');
    const [selectedEstadoFilter, setSelectedEstadoFilter] = useState<string>('todos');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [toastMessage, setToastMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

    // Estados del calendario
    const [view, setView] = useState<View>(Views.MONTH);
    const [date, setDate] = useState<Date>(new Date());

    // Estados de Modales
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
    const [summaryEvents, setSummaryEvents] = useState<CalendarEvent[]>([]);

    // Modal de Cita Rápida / Observaciones
    const [selectedCita, setSelectedCita] = useState<Cita | null>(null);
    const [isCitaModalOpen, setIsCitaModalOpen] = useState(false);
    const [modalActiveTab, setModalActiveTab] = useState<'info' | 'observaciones'>('info');

    // Estado del formulario de observaciones en el modal rápido
    const [obsIngreso, setObsIngreso] = useState<string>('');
    const [obsSalida, setObsSalida] = useState<string>('');
    const [limpIngreso, setLimpIngreso] = useState<boolean>(true);
    const [limpSalida, setLimpSalida] = useState<boolean>(true);
    const [isSavingObs, setIsSavingObs] = useState(false);

    // Notificación Toast autolimpiable
    const showToast = useCallback((text: string, type: 'success' | 'error' = 'success') => {
        setToastMessage({ text, type });
        setTimeout(() => setToastMessage(null), 3500);
    }, []);

    // Cargar Citas y Sucursales
    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [citasData, sucursalesData] = await Promise.all([
                fetchCitas(selectedSucursalId !== 'todas' ? selectedSucursalId : undefined),
                fetchSucursales().catch(() => [])
            ]);
            setCitas(citasData);
            setSucursales(sucursalesData.filter((s: Sucursal) => s.activo));
        } catch (error) {
            console.error('Error cargando citas del calendario:', error);
            showToast('Error al cargar las reservas', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [selectedSucursalId, showToast]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // WebSocket para sincronización en tiempo real
    const handleCitaSocketEvent = useCallback((action: string, citaData: any) => {
        if (action === 'create') {
            setCitas(prev => {
                if (prev.some(c => c.id === citaData.id)) return prev;
                return [citaData, ...prev];
            });
        } else if (action === 'update') {
            setCitas(prev => prev.map(c => c.id === citaData.id ? { ...c, ...citaData } : c));
        } else if (action === 'delete') {
            setCitas(prev => prev.filter(c => c.id !== citaData.id));
        }
    }, []);

    useWebSocketCitas(handleCitaSocketEvent);

    const sumarHora = useCallback((timeStr: string, horas: number): string => {
        const [h, m] = timeStr.split(':').map(Number);
        const totalMinutos = h * 60 + m + horas * 60;
        const newH = Math.floor(totalMinutos / 60) % 24;
        const newM = totalMinutos % 60;
        return `${newH.toString().padStart(2, '0')}:${newM.toString().padStart(2, '0')}`;
    }, []);

    // -----------------------------------------------------------------
    // Filtrado de Citas y Mapeo a Eventos del Calendario
    // -----------------------------------------------------------------
    const filteredCitas = useMemo(() => {
        return citas.filter(cita => {
            // Filtro por sucursal
            if (selectedSucursalId !== 'todas' && cita.sucursal !== selectedSucursalId) {
                return false;
            }

            // Filtro por estado
            if (selectedEstadoFilter !== 'todos') {
                const estadoLower = (cita.estado || '').toLowerCase();
                const ejecucionLower = (cita.estado_ejecucion || '').toLowerCase();
                if (selectedEstadoFilter === 'confirmada' && estadoLower !== 'confirmada') return false;
                if (selectedEstadoFilter === 'pendiente' && estadoLower !== 'pendiente') return false;
                if (selectedEstadoFilter === 'cancelada' && estadoLower !== 'cancelada') return false;
                if (selectedEstadoFilter === 'encurso' && ejecucionLower !== 'encurso') return false;
                if (selectedEstadoFilter === 'limpieza' && (cita.limpieza_ingreso === false || cita.limpieza_salida === false)) return true;
                if (selectedEstadoFilter === 'con_observaciones' && (!cita.observaciones_ingreso && !cita.observaciones_salida)) return false;
            }

            // Filtro por búsqueda rápida
            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const clienteNombre = cita.cliente_info
                    ? `${cita.cliente_info.first_name} ${cita.cliente_info.last_name}`.toLowerCase()
                    : '';
                const codigo = (cita.codigo || '').toLowerCase();
                const servicio = (cita.servicio_info?.nombre || '').toLowerCase();
                const espacio = (cita.espacio_info?.nombre || '').toLowerCase();
                const sucursal = (cita.sucursal_info?.nombre || '').toLowerCase();

                return clienteNombre.includes(term) ||
                    codigo.includes(term) ||
                    servicio.includes(term) ||
                    espacio.includes(term) ||
                    sucursal.includes(term);
            }

            return true;
        });
    }, [citas, selectedSucursalId, selectedEstadoFilter, searchTerm]);

    // Estadísticas para los badges superiores
    const stats = useMemo(() => {
        const total = citas.length;
        const confirmadas = citas.filter(c => (c.estado || '').toLowerCase() === 'confirmada').length;
        const pendientes = citas.filter(c => (c.estado || '').toLowerCase() === 'pendiente').length;
        const enCurso = citas.filter(c => (c.estado_ejecucion || '').toLowerCase() === 'encurso').length;
        const conObservaciones = citas.filter(c => !!c.observaciones_ingreso || !!c.observaciones_salida).length;
        return { total, confirmadas, pendientes, enCurso, conObservaciones };
    }, [citas]);

    const events = useMemo<CalendarEvent[]>(() => {
        const eventos: CalendarEvent[] = [];

        filteredCitas
            .filter(cita => cita.fecha_inicio)
            .forEach(cita => {
                const allDay = !cita.hora_inicio && !cita.hora_fin;

                let start: Date;
                let end: Date;

                if (allDay) {
                    start = combineDateAndTime(cita.fecha_inicio!);
                    start.setHours(0, 0, 0, 0);
                    end = combineDateAndTime(cita.fecha_fin || cita.fecha_inicio!);
                    end.setHours(23, 59, 59, 999);
                } else {
                    start = combineDateAndTime(
                        cita.fecha_inicio!,
                        cita.hora_inicio
                    );
                    end = combineDateAndTime(
                        cita.fecha_fin || cita.fecha_inicio!,
                        cita.hora_fin || sumarHora(cita.hora_inicio!, 1)
                    );
                }

                const clienteNombre = cita.cliente_info
                    ? `${cita.cliente_info.first_name} ${cita.cliente_info.last_name}`
                    : 'Cliente sin nombre';
                const codigo = cita.codigo || `CITA-${cita.id}`;

                eventos.push({
                    id: `${cita.id}`,
                    title: `${codigo} - ${clienteNombre}`,
                    start,
                    end,
                    allDay,
                    resource: cita,
                });
            });

        return eventos;
    }, [filteredCitas, sumarHora]);

    // -----------------------------------------------------------------
    // Colores y Estilos de Eventos (Pasantías)
    // -----------------------------------------------------------------
    const eventStyleGetter = useCallback((event: any) => {
        const citaEvent = event as CalendarEvent;
        const cita = citaEvent.resource;
        const estado = (cita?.estado || '').toLowerCase();
        const ejecucion = (cita?.estado_ejecucion || '').toLowerCase();

        let bgGradient = 'linear-gradient(135deg, #61257b, #4a1860)';
        let borderLeftColor = '#c1348b';

        if (estado === 'confirmada' || estado === 'completada') {
            if (ejecucion === 'encurso') {
                bgGradient = 'linear-gradient(135deg, #0d9488, #0f766e)';
                borderLeftColor = '#2dd4bf';
            } else {
                bgGradient = 'linear-gradient(135deg, #059669, #047857)';
                borderLeftColor = '#34d399';
            }
        } else if (estado === 'pendiente') {
            bgGradient = 'linear-gradient(135deg, #ea8406, #c2410c)';
            borderLeftColor = '#ffd97c';
        } else if (estado === 'cancelada') {
            bgGradient = 'linear-gradient(135deg, #e11d48, #9f1239)';
            borderLeftColor = '#fca5a5';
        }

        const isMonth = view === Views.MONTH;

        return {
            style: {
                background: bgGradient,
                border: 'none',
                borderLeft: isMonth ? `3px solid ${borderLeftColor}` : `4px solid ${borderLeftColor}`,
                color: 'white',
                borderRadius: isMonth ? '6px' : '9px',
                padding: isMonth ? '1px 5px' : '4px 8px',
                margin: isMonth ? '1px 0' : '2px 0',
                boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
            },
        };
    }, [view]);

    // -----------------------------------------------------------------
    // Componente Personalizado para Tarjeta de Evento
    // Normalizado: Chip compacto de 1 línea en Vista Mes (~22px)
    // Tarjeta detallada completa en Vistas Semana / Día / Agenda
    // -----------------------------------------------------------------
    const EventComponent = useCallback(({ event }: { event: any }) => {
        const citaEvent = event as CalendarEvent;
        const cita = citaEvent.resource;
        if (!cita) return null;

        const estado = (cita.estado || '').toLowerCase();
        const ejecucion = (cita.estado_ejecucion || '').toLowerCase();
        const hasObservations = !!(cita.observaciones_ingreso || cita.observaciones_salida);
        const hasCleanlinessCheck = cita.limpieza_ingreso !== undefined || cita.limpieza_salida !== undefined;
        const allClean = (cita.limpieza_ingreso ?? true) && (cita.limpieza_salida ?? true);

        // ── VISTA MENSUAL: Normalizado a 1 sola línea compacta ──────────
        // Permite que 3-4 reservas se vean nítidamente por día sin colapsar ni cortarse
        if (view === Views.MONTH) {
            const shortCode = cita.codigo ? cita.codigo.replace('CM/26/', '') : `CITA-${cita.id}`;
            const clienteName = cita.cliente_info
                ? `${cita.cliente_info.first_name} ${cita.cliente_info.last_name}`
                : 'Cliente';

            return (
                <div
                    className="flex items-center gap-1.5 w-full select-none overflow-hidden"
                    style={{ height: '22px', fontSize: '0.72rem', lineHeight: '1.2' }}
                    title={`${cita.codigo || `CITA-${cita.id}`} • ${clienteName} • ${cita.servicio_info?.nombre || cita.espacio_info?.nombre || 'Reserva'} ${cita.hora_inicio ? `(${cita.hora_inicio.substring(0, 5)})` : ''}`}
                >
                    <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{
                            background:
                                estado === 'confirmada' || estado === 'completada'
                                    ? (ejecucion === 'encurso' ? '#38bdf8' : '#34d399')
                                    : estado === 'pendiente'
                                    ? '#fbbf24'
                                    : estado === 'cancelada'
                                    ? '#f87171'
                                    : '#ffffff',
                            boxShadow: '0 0 4px rgba(255,255,255,0.7)'
                        }}
                    />
                    {!citaEvent.allDay && cita.hora_inicio && (
                        <span className="font-mono text-[10px] font-bold text-white/90 flex-shrink-0">
                            {cita.hora_inicio.substring(0, 5)}
                        </span>
                    )}
                    <span className="font-extrabold text-[10.5px] tracking-tight text-white flex-shrink-0">
                        {shortCode}
                    </span>
                    <span className="text-[10.5px] font-medium text-white/95 truncate">
                        {clienteName}
                    </span>
                    {hasObservations && (
                        <span className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-amber-400 text-amber-950 flex-shrink-0 text-[8px] font-black ml-auto">
                            i
                        </span>
                    )}
                    {hasCleanlinessCheck && allClean && (
                        <span className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-emerald-400 text-emerald-950 flex-shrink-0 ml-auto">
                            <Sparkles size={7} />
                        </span>
                    )}
                </div>
            );
        }

        // ── VISTAS SEMANA, DÍA Y AGENDA: Tarjeta expandida completa ─────
        return (
            <div className="flex flex-col gap-0.5 select-none leading-tight py-0.5">
                <div className="flex items-center justify-between gap-1 overflow-hidden">
                    <span className="font-extrabold text-[0.74rem] tracking-tight truncate text-white">
                        {cita.codigo || `CITA-${cita.id}`}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {hasObservations && (
                            <span
                                title="Tiene observaciones de ingreso o salida"
                                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-amber-400 text-amber-950 text-[9px] font-bold"
                            >
                                i
                            </span>
                        )}
                        {hasCleanlinessCheck && allClean && (
                            <span
                                title="Espacio verificado limpio"
                                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-400 text-emerald-950"
                            >
                                <Sparkles size={8} />
                            </span>
                        )}
                    </div>
                </div>

                <div className="text-[0.78rem] font-bold truncate text-white">
                    {cita.cliente_info ? `${cita.cliente_info.first_name} ${cita.cliente_info.last_name}` : 'Cliente no asignado'}
                </div>

                <div className="flex items-center justify-between text-[0.7rem] text-white/85 font-medium">
                    <span className="truncate">
                        {cita.servicio_info?.nombre || (cita.espacio_info ? cita.espacio_info.nombre : 'Sin servicio')}
                    </span>
                    {!citaEvent.allDay && cita.hora_inicio && (
                        <span className="text-[0.68rem] font-mono ml-1 text-white font-bold whitespace-nowrap">
                            {cita.hora_inicio.substring(0, 5)}
                        </span>
                    )}
                </div>
                {cita.plan_info && (
                    <div className="text-[0.64rem] text-white/70 truncate font-semibold">
                        {cita.plan_info.plan_nombre}
                    </div>
                )}
            </div>
        );
    }, [view]);

    // -----------------------------------------------------------------
    // Drag & Drop con Persistencia Real en Backend
    // -----------------------------------------------------------------
    const handleEventDrop = useCallback(async ({ event, start, end, isAllDay: droppedIsAllDay }: any) => {
        const cita = (event as CalendarEvent).resource;
        if (!cita || !cita.id) return;

        const hoy = startOfDay(new Date());
        if (start < hoy) {
            showToast('No se puede reprogramar una reserva a una fecha pasada', 'error');
            return;
        }

        const previousCitas = [...citas];
        const newFechaInicio = format(start, 'yyyy-MM-dd');
        const newFechaFin = format(end, 'yyyy-MM-dd');
        const newHoraInicio = format(start, 'HH:mm:ss');
        const newHoraFin = format(end, 'HH:mm:ss');

        const isAllDay = (event as CalendarEvent).allDay || droppedIsAllDay || (!cita.hora_inicio && !cita.hora_fin);

        if (!requireGrant(cita)) return;
        // Optimistic UI update
        if (!requireGrant(cita)) return;
        setCitas(prev =>
            prev.map(c =>
                c.id === cita.id
                    ? {
                        ...c,
                        fecha_inicio: newFechaInicio,
                        fecha_fin: newFechaFin,
                        hora_inicio: isAllDay ? c.hora_inicio : newHoraInicio,
                        hora_fin: isAllDay ? c.hora_fin : newHoraFin,
                    }
                    : c
            )
        );

        setIsSaving(true);
        try {
            await patchCita(cita.id, {
                fecha_inicio: newFechaInicio,
                fecha_fin: newFechaFin,
                ...(isAllDay ? {} : { hora_inicio: newHoraInicio, hora_fin: newHoraFin })
            }, operador.token);
            showToast(`Reserva ${cita.codigo || ''} movida a ${newFechaInicio}`);
        } catch (error: any) {
            console.error('Error al guardar movimiento de cita:', error);
            // Rollback en caso de error
            setCitas(previousCitas);
            showToast(error.response?.data?.error || 'Error al reprogramar la reserva en el servidor', 'error');
        } finally {
            setIsSaving(false);
        }
    }, [citas, showToast, pinSesionId, operador.token]);

    const handleEventResize = useCallback(async ({ event, start, end }: any) => {
        const cita = (event as CalendarEvent).resource;
        if (!cita || !cita.id) return;

        const hoy = startOfDay(new Date());
        if (start < hoy) {
            showToast('No se puede extender o mover una reserva a una fecha pasada', 'error');
            return;
        }

        const previousCitas = [...citas];
        const newFechaInicio = format(start, 'yyyy-MM-dd');
        const newFechaFin = format(end, 'yyyy-MM-dd');
        const newHoraInicio = format(start, 'HH:mm:ss');
        const newHoraFin = format(end, 'HH:mm:ss');

        const isAllDay = (event as CalendarEvent).allDay || (!cita.hora_inicio && !cita.hora_fin);

        if (!requireGrant(cita)) return;
        setCitas(prev =>
            prev.map(c =>
                c.id === cita.id
                    ? {
                        ...c,
                        fecha_inicio: newFechaInicio,
                        fecha_fin: newFechaFin,
                        hora_inicio: isAllDay ? c.hora_inicio : newHoraInicio,
                        hora_fin: isAllDay ? c.hora_fin : newHoraFin,
                    }
                    : c
            )
        );

        setIsSaving(true);
        try {
            await patchCita(cita.id, {
                fecha_inicio: newFechaInicio,
                fecha_fin: newFechaFin,
                ...(isAllDay ? {} : { hora_inicio: newHoraInicio, hora_fin: newHoraFin })
            }, operador.token);
            showToast(`Duración actualizada: ${newFechaInicio} a ${newFechaFin}`);
        } catch (error: any) {
            console.error('Error al redimensionar cita:', error);
            setCitas(previousCitas);
            showToast(error.response?.data?.error || 'Error al actualizar duración', 'error');
        } finally {
            setIsSaving(false);
        }
    }, [citas, showToast, pinSesionId, operador.token]);

    // -----------------------------------------------------------------
    // Selección de Slot para Crear Cita
    // -----------------------------------------------------------------
    const handleSelectSlot = useCallback(({ start, end }: { start: Date; end: Date }) => {
        const hoy = startOfDay(new Date());
        if (start < hoy) {
            showToast('No se pueden realizar reservas en fechas pasadas. Selecciona desde hoy en adelante.', 'error');
            return;
        }

        const startStr = format(start, 'yyyy-MM-dd');
        const endStr = format(end, 'yyyy-MM-dd');
        const horaInicio = format(start, 'HH:mm');
        const horaFin = format(end, 'HH:mm');

        // Navegar directamente a Point Of Sale con las fechas seleccionadas
        navigate(`/point-of-sale?fecha_inicio=${startStr}&fecha_fin=${endStr}&hora_inicio=${horaInicio}&hora_fin=${horaFin}`);
    }, [navigate, showToast]);

    // -----------------------------------------------------------------
    // Clic en Evento: Abrir Modal Rápido con Observaciones y Acciones
    // -----------------------------------------------------------------
    const handleSelectEvent = useCallback((event: any) => {
        const calendarEvent = event as CalendarEvent;
        if (!calendarEvent.resource) return;

        const cita = calendarEvent.resource;
        setSelectedCita(cita);
        setObsIngreso(cita.observaciones_ingreso || '');
        setObsSalida(cita.observaciones_salida || '');
        setLimpIngreso(cita.limpieza_ingreso ?? true);
        setLimpSalida(cita.limpieza_salida ?? true);
        setModalActiveTab('info');
        setIsCitaModalOpen(true);
    }, []);

    // Guardar Observaciones y Limpieza desde el Modal Rápido
    const handleSaveObservaciones = async () => {
        if (!selectedCita || !requireGrant(selectedCita)) return;

        setIsSavingObs(true);
        try {
            const updated = await patchCita(selectedCita.id, {
                observaciones_ingreso: obsIngreso,
                observaciones_salida: obsSalida,
                limpieza_ingreso: limpIngreso,
                limpieza_salida: limpSalida
            }, operador.token);

            // Actualizar estado local
            setCitas(prev => prev.map(c => c.id === selectedCita.id ? { ...c, ...updated } : c));
            setSelectedCita(prev => prev ? { ...prev, ...updated } : null);
            showToast('Observaciones y estado de limpieza guardados correctamente');
            setIsCitaModalOpen(false); // Vuelve a la vista del calendario
        } catch (error) {
            console.error('Error al guardar observaciones:', error);
            showToast('No se pudieron guardar las observaciones', 'error');
        } finally {
            setIsSavingObs(false);
        }
    };

    // -----------------------------------------------------------------
    // Navegación personalizada del Calendario
    // -----------------------------------------------------------------
    const handleNavigate = useCallback(
        (action: 'TODAY' | 'PREV' | 'NEXT') => {
            const newDate = new Date(date);
            if (action === 'TODAY') {
                newDate.setTime(new Date().getTime());
            } else if (action === 'PREV') {
                if (view === Views.MONTH) newDate.setMonth(newDate.getMonth() - 1);
                else if (view === Views.WEEK) newDate.setDate(newDate.getDate() - 7);
                else if (view === Views.DAY) newDate.setDate(newDate.getDate() - 1);
                else if (view === Views.AGENDA) newDate.setDate(newDate.getDate() - 7);
            } else if (action === 'NEXT') {
                if (view === Views.MONTH) newDate.setMonth(newDate.getMonth() + 1);
                else if (view === Views.WEEK) newDate.setDate(newDate.getDate() + 7);
                else if (view === Views.DAY) newDate.setDate(newDate.getDate() + 1);
                else if (view === Views.AGENDA) newDate.setDate(newDate.getDate() + 7);
            }
            setDate(newDate);
        },
        [date, view]
    );

    // Título formateado de fecha según la vista activa
    const formattedDateTitle = useMemo(() => {
        if (view === Views.MONTH) {
            const str = format(date, 'MMMM yyyy', { locale: es });
            return str.charAt(0).toUpperCase() + str.slice(1);
        }
        if (view === Views.WEEK) {
            const start = startOfWeek(date, { weekStartsOn: 1 });
            const end = endOfWeek(date, { weekStartsOn: 1 });
            return `${format(start, 'd MMM', { locale: es })} - ${format(end, 'd MMM, yyyy', { locale: es })}`;
        }
        if (view === Views.DAY) {
            const str = format(date, "EEEE d 'de' MMMM, yyyy", { locale: es });
            return str.charAt(0).toUpperCase() + str.slice(1);
        }
        return `Agenda a partir de ${format(date, 'd MMMM yyyy', { locale: es })}`;
    }, [date, view]);

    // Modal "Ver Resumen / Listado"
    const handleOpenSummaryModal = useCallback(() => {
        let startRange: Date, endRange: Date;

        switch (view) {
            case Views.MONTH:
                startRange = startOfMonth(date);
                endRange = endOfMonth(date);
                break;
            case Views.WEEK:
                startRange = startOfWeek(date, { weekStartsOn: 1 });
                endRange = endOfWeek(date, { weekStartsOn: 1 });
                break;
            case Views.DAY:
                startRange = startOfDay(date);
                endRange = endOfDay(date);
                break;
            case Views.AGENDA:
                startRange = startOfDay(date);
                endRange = endOfDay(addDays(date, 30));
                break;
            default:
                startRange = startOfMonth(date);
                endRange = endOfMonth(date);
        }

        const filtered = events.filter(event => {
            const eventStart = event.start;
            const eventEnd = event.end;
            return eventStart <= endRange && eventEnd >= startRange;
        });

        setSummaryEvents(filtered);
        setIsSummaryModalOpen(true);
    }, [view, date, events]);

    return (
        <>
        <OperadorPinDialog open={operador.dialogOpen} sesionId={pinSesionId} onClose={operador.closeDialog} onGrant={operador.install} />
        <>
            <style>{calendarCss}</style>

            <div className="rc-page min-h-screen bg-[#f8fafc] p-4 sm:p-6 lg:p-8 space-y-5">
                {/* Toast Flotante */}
                {toastMessage && (
                    <div className="fixed bottom-6 right-6 z-50 rc-fade">
                        <div
                            className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl text-white font-semibold text-sm ${
                                toastMessage.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
                            }`}
                        >
                            {toastMessage.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                            <span>{toastMessage.text}</span>
                        </div>
                    </div>
                )}

                {/* Header Superior con Breadcrumbs */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 sm:p-6 rounded-2xl border border-slate-100 shadow-sm rc-fade">
                    <div>
                        <nav className="flex items-center gap-2 text-xs font-semibold text-slate-400 mb-1.5">
                            <Link to="/dashboard" className="flex items-center gap-1.5 hover:text-[#61257b] transition-colors">
                                <Home size={13} /> Dashboard
                            </Link>
                            <ChevronRight size={12} />
                            <span className="text-[#61257b] font-bold">Calendario de Reservas</span>
                        </nav>
                        <div className="flex items-center gap-3">
                            <div
                                className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm"
                                style={{ background: `linear-gradient(135deg, ${colors.brandDark}, ${colors.brand})` }}
                            >
                                <CalendarIcon size={20} />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                                    Calendario de <span style={{ color: colors.brand }}>Reservas</span>
                                    {isSaving && (
                                        <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1">
                                            <RefreshCw size={11} className="animate-spin" /> Guardando...
                                        </span>
                                    )}
                                </h1>
                                <p className="text-xs sm:text-sm text-slate-500 font-medium">
                                    Visualiza, reprograma y gestiona el ingreso, limpieza y salida de tus clientes
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Acciones Principales */}
                    <div className="flex items-center flex-wrap gap-2.5">
                        <Link
                            to="/reservas"
                            className="px-4 py-2.5 text-xs sm:text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all flex items-center gap-2 shadow-sm"
                        >
                            <List size={15} /> Ver Lista
                        </Link>
                        <Link
                            to="/point-of-sale"
                            className="px-5 py-2.5 text-xs sm:text-sm font-extrabold text-white rounded-xl shadow-md transition-all flex items-center gap-2 hover:opacity-95 hover:shadow-lg"
                            style={{ background: `linear-gradient(135deg, ${colors.brand}, ${colors.accent1})` }}
                        >
                            <Plus size={16} /> Nueva Reserva
                        </Link>
                    </div>
                </div>

                {/* Barra de Filtros y Contadores de Estado */}
                <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-100 shadow-sm space-y-4 rc-fade">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Selector de Sucursal y Buscador */}
                        <div className="flex items-center flex-wrap gap-3 flex-1">
                            {/* Sucursales */}
                            <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-xl border border-slate-200">
                                <Building2 size={16} className="text-slate-400 flex-shrink-0" />
                                <select
                                    value={selectedSucursalId}
                                    onChange={e => setSelectedSucursalId(e.target.value === 'todas' ? 'todas' : Number(e.target.value))}
                                    className="bg-transparent text-xs sm:text-sm font-bold text-slate-800 focus:outline-none cursor-pointer"
                                >
                                    <option value="todas">Todas las Sucursales</option>
                                    {sucursales.map(s => (
                                        <option key={s.id} value={s.id}>
                                            {s.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Búsqueda rápida */}
                            <div className="relative flex-1 min-w-[200px] max-w-md">
                                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Buscar por cliente, código, servicio o espacio..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-8 py-2 text-xs sm:text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-[#61257b] focus:outline-none transition-all"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Botón de Resumen / Listado Expandido */}
                        <button
                            onClick={handleOpenSummaryModal}
                            className="px-4 py-2 text-xs sm:text-sm font-bold text-[#61257b] bg-[#61257b]/10 hover:bg-[#61257b]/20 border border-[#61257b]/20 rounded-xl transition-all flex items-center justify-center gap-2 flex-shrink-0"
                        >
                            <Layers size={15} /> Resumen del Período ({events.length})
                        </button>
                    </div>

                    {/* Chips Filtros Rápidos por Estado */}
                    <div className="flex items-center flex-wrap gap-2 pt-2 border-t border-slate-100">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mr-1">Filtrar por:</span>
                        {[
                            { id: 'todos', label: 'Todas', count: stats.total, color: 'bg-slate-100 text-slate-700' },
                            { id: 'confirmada', label: 'Confirmadas', count: stats.confirmadas, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                            { id: 'pendiente', label: 'Pendientes', count: stats.pendientes, color: 'bg-amber-50 text-amber-700 border-amber-200' },
                            { id: 'encurso', label: 'En Curso', count: stats.enCurso, color: 'bg-teal-50 text-teal-700 border-teal-200' },
                            { id: 'con_observaciones', label: 'Con Observaciones', count: stats.conObservaciones, color: 'bg-purple-50 text-purple-700 border-purple-200' }
                        ].map(chip => (
                            <button
                                key={chip.id}
                                onClick={() => setSelectedEstadoFilter(chip.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 border ${
                                    selectedEstadoFilter === chip.id
                                        ? 'bg-[#61257b] text-white border-[#61257b] shadow-sm'
                                        : `${chip.color} hover:bg-slate-200 border-transparent`
                                }`}
                            >
                                <span>{chip.label}</span>
                                <span className={`px-1.5 py-0.2 rounded-full text-[0.65rem] ${
                                    selectedEstadoFilter === chip.id ? 'bg-white/20 text-white' : 'bg-black/5 text-slate-600'
                                }`}>
                                    {chip.count}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Barra de Navegación del Calendario y Vistas */}
                <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-wrap items-center justify-between gap-4 rc-fade">
                    {/* Controles de fecha */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handleNavigate('TODAY')}
                            className="px-3.5 py-2 text-xs sm:text-sm font-bold text-slate-700 bg-slate-100 hover:bg-[#61257b]/10 hover:text-[#61257b] rounded-xl transition-all"
                        >
                            Hoy
                        </button>
                        <div className="flex items-center bg-slate-100 rounded-xl p-0.5 border border-slate-200">
                            <button
                                onClick={() => handleNavigate('PREV')}
                                className="p-1.5 text-slate-700 hover:bg-white rounded-lg transition-all"
                                title="Anterior"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                onClick={() => handleNavigate('NEXT')}
                                className="p-1.5 text-slate-700 hover:bg-white rounded-lg transition-all"
                                title="Siguiente"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                        <h2 className="text-base sm:text-lg font-black text-slate-800 ml-2 tracking-tight">
                            {formattedDateTitle}
                        </h2>
                    </div>

                    {/* Selector de Vistas */}
                    <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 gap-1">
                        {[
                            { viewName: Views.MONTH, label: 'Mes' },
                            { viewName: Views.WEEK, label: 'Semana' },
                            { viewName: Views.DAY, label: 'Día' },
                            { viewName: Views.AGENDA, label: 'Agenda' },
                        ].map(v => (
                            <button
                                key={v.viewName}
                                onClick={() => setView(v.viewName)}
                                className={`px-3.5 py-1.5 text-xs sm:text-sm font-bold rounded-lg transition-all ${
                                    view === v.viewName
                                        ? 'bg-[#61257b] text-white shadow-md'
                                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                                }`}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Contenedor del Calendario */}
                <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-100 shadow-sm rc-fade">
                    {isLoading ? (
                        <div className="h-[600px] flex flex-col items-center justify-center gap-3">
                            <div
                                className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
                                style={{ borderColor: `${colors.brand} transparent transparent transparent` }}
                            />
                            <p className="text-sm font-bold text-slate-500">Cargando reservas del calendario...</p>
                        </div>
                    ) : (
                        <div style={{ height: 'calc(100vh - 340px)', minHeight: '620px' }}>
                            <DragAndDropCalendar
                                localizer={localizer}
                                events={events}
                                startAccessor={(event: any) => (event as CalendarEvent).start}
                                endAccessor={(event: any) => (event as CalendarEvent).end}
                                allDayAccessor={(event: any) => (event as CalendarEvent).allDay}
                                style={{ height: '100%' }}
                                view={view}
                                date={date}
                                onView={setView}
                                onNavigate={setDate}
                                eventPropGetter={eventStyleGetter}
                                dayPropGetter={(d: Date) => {
                                    const hoy = startOfDay(new Date());
                                    if (startOfDay(d) < hoy) {
                                        return {
                                            style: {
                                                backgroundColor: '#f8fafc',
                                                opacity: 0.65,
                                            }
                                        };
                                    }
                                    return {};
                                }}
                                slotPropGetter={(d: Date) => {
                                    const hoy = startOfDay(new Date());
                                    if (d < hoy) {
                                        return {
                                            style: {
                                                backgroundColor: '#f8fafc',
                                                opacity: 0.65,
                                            }
                                        };
                                    }
                                    return {};
                                }}
                                components={{
                                    event: EventComponent,
                                }}
                                onEventDrop={handleEventDrop}
                                onEventResize={handleEventResize}
                                resizable
                                selectable
                                popup={true}
                                popupOffset={{ x: 10, y: 10 }}
                                onSelectSlot={handleSelectSlot}
                                onSelectEvent={handleSelectEvent}
                                toolbar={false}
                                messages={{
                                    next: 'Siguiente',
                                    previous: 'Anterior',
                                    today: 'Hoy',
                                    month: 'Mes',
                                    week: 'Semana',
                                    day: 'Día',
                                    agenda: 'Agenda',
                                    date: 'Fecha',
                                    time: 'Hora',
                                    event: 'Reserva',
                                    noEventsInRange: 'No hay reservas en este período seleccionado.',
                                    showMore: (count) => `+ ${count} más`,
                                }}
                                culture="es"
                            />
                        </div>
                    )}
                </div>

                {/* Leyenda y Atajos Rápidos */}
                <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs font-semibold text-slate-600">
                    <div className="flex items-center flex-wrap gap-4">
                        <span className="font-extrabold text-slate-800 uppercase tracking-wider text-[0.7rem]">Guía de colores:</span>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-emerald-600 shadow-sm" />
                            <span>Confirmada</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-teal-500 shadow-sm" />
                            <span>En Curso</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-amber-500 shadow-sm" />
                            <span>Pendiente</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-rose-600 shadow-sm" />
                            <span>Cancelada</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 text-slate-400">
                        <span className="flex items-center gap-1"><Sparkles size={13} className="text-emerald-500" /> Limpieza validada</span>
                        <span className="flex items-center gap-1"><Info size={13} className="text-purple-600" /> Con observaciones</span>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* MODAL RÁPIDO DE RESERVA (INFO + CONTROL DE INGRESO Y SALIDA) */}
            {/* ============================================================ */}
            {isCitaModalOpen && selectedCita && (
                <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4">
                    <div
                        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                        onClick={() => setIsCitaModalOpen(false)}
                    />

                    <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden border border-slate-100 rc-fade z-10 flex flex-col max-h-[90vh]">
                        {/* Cabecera del Modal */}
                        <div
                            className="p-6 text-white relative flex items-start justify-between"
                            style={{ background: `linear-gradient(135deg, ${colors.brandDark}, ${colors.brand})` }}
                        >
                            <div>
                                <span className="text-[0.7rem] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-white/20 text-white inline-block mb-2">
                                    {selectedCita.codigo || `CITA-${selectedCita.id}`}
                                </span>
                                <h3 className="text-xl font-black tracking-tight">
                                    {selectedCita.cliente_info
                                        ? `${selectedCita.cliente_info.first_name} ${selectedCita.cliente_info.last_name}`
                                        : 'Cliente no asignado'}
                                </h3>
                                <p className="text-xs text-white/80 font-medium mt-0.5">
                                    {selectedCita.servicio_info?.nombre || 'Servicio de reserva'}
                                    {selectedCita.plan_info && ` — ${selectedCita.plan_info.plan_nombre}`}
                                    {selectedCita.espacio_info && ` • ${selectedCita.espacio_info.nombre}`}
                                </p>
                            </div>

                            <button
                                onClick={() => setIsCitaModalOpen(false)}
                                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Pestañas del Modal */}
                        <div className="flex border-b border-slate-100 bg-slate-50/50 px-6 pt-2">
                            <button
                                onClick={() => setModalActiveTab('info')}
                                className={`pb-3 px-4 text-xs font-bold border-b-2 transition-all flex items-center gap-2 ${
                                    modalActiveTab === 'info'
                                        ? 'border-[#8f1f64] text-[#8f1f64]'
                                        : 'border-transparent text-slate-500 hover:text-slate-800'
                                }`}
                            >
                                <Info size={14} /> Resumen de Reserva
                            </button>
                            <button
                                onClick={() => setModalActiveTab('observaciones')}
                                className={`pb-3 px-4 text-xs font-bold border-b-2 transition-all flex items-center gap-2 ${
                                    modalActiveTab === 'observaciones'
                                        ? 'border-[#8f1f64] text-[#8f1f64]'
                                        : 'border-transparent text-slate-500 hover:text-slate-800'
                                }`}
                            >
                                <Sparkles size={14} /> Control de Ingreso & Salida (Limpieza)
                            </button>
                        </div>

                        {/* Contenido del Modal */}
                        <div className="p-6 overflow-y-auto custom-scroll space-y-5 flex-1">
                            {modalActiveTab === 'info' ? (
                                <div className="space-y-4">
                                    {/* Badges de Estado */}
                                    <div className="grid grid-cols-3 gap-2.5">
                                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                            <span className="text-[0.65rem] font-bold text-slate-400 uppercase">Estado</span>
                                            <p className="font-extrabold text-sm text-slate-800">{selectedCita.estado}</p>
                                        </div>
                                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                            <span className="text-[0.65rem] font-bold text-slate-400 uppercase">Pago</span>
                                            <p className="font-extrabold text-sm text-slate-800">{selectedCita.estado_pago}</p>
                                        </div>
                                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                            <span className="text-[0.65rem] font-bold text-slate-400 uppercase">Ejecución</span>
                                            <p className="font-extrabold text-sm text-slate-800">{selectedCita.estado_ejecucion}</p>
                                        </div>
                                    </div>

                                    {/* Detalles de Fecha y Horario */}
                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                                                <CalendarIcon size={16} />
                                            </div>
                                            <div>
                                                <span className="text-[0.7rem] font-bold text-slate-400">Fecha Programada</span>
                                                <p className="text-xs sm:text-sm font-bold text-slate-800">
                                                    {selectedCita.fecha_inicio}
                                                    {selectedCita.fecha_fin && selectedCita.fecha_fin !== selectedCita.fecha_inicio && ` → ${selectedCita.fecha_fin}`}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                                                <Clock size={16} />
                                            </div>
                                            <div>
                                                <span className="text-[0.7rem] font-bold text-slate-400">Horario</span>
                                                <p className="text-xs sm:text-sm font-bold text-slate-800">
                                                    {selectedCita.hora_inicio ? selectedCita.hora_inicio.substring(0, 5) : 'Sin hora'}
                                                    {selectedCita.hora_fin && ` - ${selectedCita.hora_fin.substring(0, 5)}`}
                                                </p>
                                            </div>
                                        </div>

                                        {selectedCita.espacio_info && (
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                                                    <MapPin size={16} />
                                                </div>
                                                <div>
                                                    <span className="text-[0.7rem] font-bold text-slate-400">Espacio Asignado</span>
                                                    <p className="text-xs sm:text-sm font-bold text-slate-800">
                                                        {selectedCita.espacio_info.nombre}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Resumen Financiero */}
                                    <div className="flex items-center justify-between p-4 bg-slate-900 text-white rounded-2xl">
                                        <div>
                                            <span className="text-[0.7rem] font-medium text-slate-400">Monto Total</span>
                                            <p className="text-xl font-black">Bs {selectedCita.total}</p>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-[0.7rem] font-medium text-slate-400">Pagado</span>
                                            <p className="text-base font-extrabold text-emerald-400">Bs {selectedCita.monto_pagado || 0}</p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Pestaña de Observaciones y Control de Limpieza */
                                <div className="space-y-5">
                                    {/* Control de Ingreso */}
                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                                                    <CheckCircle2 size={15} />
                                                </div>
                                                <span className="text-xs sm:text-sm font-extrabold text-slate-800">Ingreso del Cliente</span>
                                            </div>
                                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={limpIngreso}
                                                    onChange={e => setLimpIngreso(e.target.checked)}
                                                    className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300"
                                                />
                                                <span className="text-xs font-bold text-slate-700">Espacio limpio al ingresar</span>
                                            </label>
                                        </div>

                                        <textarea
                                            placeholder="Observaciones de ingreso (estado inicial del espacio, notas de recepción, equipamiento)..."
                                            value={obsIngreso}
                                            onChange={e => setObsIngreso(e.target.value)}
                                            rows={3}
                                            className="w-full text-xs sm:text-sm p-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#8f1f64] transition-all resize-none"
                                        />
                                    </div>

                                    {/* Control de Salida */}
                                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                                                    <ShieldCheck size={15} />
                                                </div>
                                                <span className="text-xs sm:text-sm font-extrabold text-slate-800">Salida del Cliente</span>
                                            </div>
                                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={limpSalida}
                                                    onChange={e => setLimpSalida(e.target.checked)}
                                                    className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300"
                                                />
                                                <span className="text-xs font-bold text-slate-700">Espacio entregado limpio</span>
                                            </label>
                                        </div>

                                        <textarea
                                            placeholder="Observaciones de salida (estado final en que dejó el espacio, llaves, orden)..."
                                            value={obsSalida}
                                            onChange={e => setObsSalida(e.target.value)}
                                            rows={3}
                                            className="w-full text-xs sm:text-sm p-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#8f1f64] transition-all resize-none"
                                        />
                                    </div>

                                    {/* Botón Guardar Observaciones */}
                                    <button
                                        onClick={handleSaveObservaciones}
                                        disabled={isSavingObs}
                                        className="w-full py-3 bg-[#8f1f64] hover:bg-[#c1348b] text-white font-extrabold text-xs sm:text-sm rounded-xl transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                                    >
                                        {isSavingObs ? <RefreshCw size={15} className="animate-spin" /> : <Save size={15} />}
                                        {isSavingObs ? 'Guardando...' : 'Guardar Observaciones y Limpieza'}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Footer con Enlaces Directos */}
                        <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap">
                            <button
                                onClick={() => setIsCitaModalOpen(false)}
                                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200/60 rounded-xl transition-colors"
                            >
                                Cerrar
                            </button>

                            <div className="flex items-center gap-2">
                                <Link
                                    to={`/historial-pagos/cita/${selectedCita.id}`}
                                    className="px-3.5 py-2 text-xs font-bold text-emerald-700 bg-emerald-100/60 hover:bg-emerald-100 rounded-xl transition-colors flex items-center gap-1.5"
                                >
                                    <FileText size={13} /> Pagos
                                </Link>
                                <Link
                                    to={`/reservas/editar/${selectedCita.id}`}
                                    className="px-3.5 py-2 text-xs font-bold text-blue-700 bg-blue-100/60 hover:bg-blue-100 rounded-xl transition-colors flex items-center gap-1.5"
                                >
                                    <Edit size={13} /> Editar
                                </Link>
                                <Link
                                    to={`/reservas/detalle/${selectedCita.id}`}
                                    className="px-4 py-2 text-xs font-extrabold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition-colors flex items-center gap-1.5 shadow-sm"
                                >
                                    <Eye size={13} /> Ver Detalle <ArrowUpRight size={13} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================================ */}
            {/* MODAL DE RESUMEN Y LISTADO EXPANDIDO DEL PERÍODO             */}
            {/* ============================================================ */}
            {isSummaryModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4">
                    <div
                        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                        onClick={() => setIsSummaryModalOpen(false)}
                    />

                    <div className="relative bg-white rounded-3xl shadow-2xl max-w-5xl w-full overflow-hidden border border-slate-100 rc-fade z-10 flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                            <div>
                                <h3 className="text-lg font-black text-slate-900 tracking-tight">
                                    Resumen de Reservas del Período
                                </h3>
                                <p className="text-xs text-slate-500 font-medium mt-0.5">
                                    {formattedDateTitle} • {summaryEvents.length} reservas registradas
                                </p>
                            </div>
                            <button
                                onClick={() => setIsSummaryModalOpen(false)}
                                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-auto custom-scroll flex-1">
                            {summaryEvents.length === 0 ? (
                                <div className="text-center py-12">
                                    <CalendarIcon size={36} className="text-slate-300 mx-auto mb-2" />
                                    <p className="text-sm font-bold text-slate-600">No hay reservas en este período</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <AdminTable className="w-full text-left text-xs">
                                        <thead className="bg-slate-50 text-slate-400 uppercase font-extrabold tracking-wider border-b border-slate-200">
                                            <tr>
                                                <th className="py-3 px-3">Código</th>
                                                <th className="py-3 px-3">Cliente</th>
                                                <th className="py-3 px-3">Fecha</th>
                                                <th className="py-3 px-3">Horario</th>
                                                <th className="py-3 px-3">Servicio / Espacio</th>
                                                <th className="py-3 px-3">Estado</th>
                                                <th className="py-3 px-3">Limpieza / Obs.</th>
                                                <th className="py-3 px-3 text-right">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 font-medium">
                                            {summaryEvents.map(ev => {
                                                const c = ev.resource!;
                                                const estado = (c.estado || '').toLowerCase();
                                                const estadoColor =
                                                    estado === 'confirmada'
                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                        : estado === 'pendiente'
                                                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                        : estado === 'cancelada'
                                                        ? 'bg-red-50 text-red-700 border-red-200'
                                                        : 'bg-slate-100 text-slate-700';

                                                return (
                                                    <tr key={ev.id} className="hover:bg-slate-50/80 transition-colors">
                                                        <td className="py-3 px-3 font-bold text-slate-900">
                                                            {c.codigo || `CITA-${c.id}`}
                                                        </td>
                                                        <td className="py-3 px-3 font-semibold text-slate-800">
                                                            {c.cliente_info ? `${c.cliente_info.first_name} ${c.cliente_info.last_name}` : 'Sin cliente'}
                                                        </td>
                                                        <td className="py-3 px-3 text-slate-600">
                                                            {format(ev.start, 'dd/MM/yyyy')}
                                                        </td>
                                                        <td className="py-3 px-3 text-slate-600">
                                                            {ev.allDay ? 'Todo el día' : c.hora_inicio ? c.hora_inicio.substring(0, 5) : '-'}
                                                        </td>
                                                        <td className="py-3 px-3 text-slate-700">
                                                            {c.servicio_info?.nombre || (c.espacio_info ? c.espacio_info.nombre : '-')}
                                                        </td>
                                                        <td className="py-3 px-3">
                                                            <span className={`px-2.5 py-1 rounded-full text-[0.68rem] font-bold border ${estadoColor}`}>
                                                                {c.estado}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-3">
                                                            {(c.observaciones_ingreso || c.observaciones_salida) ? (
                                                                <span className="inline-flex items-center gap-1 text-[0.68rem] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-200">
                                                                    <Info size={10} /> Con Obs.
                                                                </span>
                                                            ) : (
                                                                <span className="text-slate-400 text-[0.7rem]">-</span>
                                                            )}
                                                        </td>
                                                        <td className="py-3 px-3 text-right">
                                                            <div className="flex items-center justify-end gap-1.5">
                                                                <button
                                                                    onClick={() => {
                                                                        setIsSummaryModalOpen(false);
                                                                        handleSelectEvent(ev);
                                                                    }}
                                                                    className="p-1.5 text-[#61257b] hover:bg-blue-50 rounded-lg transition-colors"
                                                                    title="Ver observaciones y ficha rápida"
                                                                >
                                                                    <Eye size={14} />
                                                                </button>
                                                                <Link
                                                                    to={`/reservas/detalle/${c.id}`}
                                                                    className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                                                    title="Ir a detalle completo"
                                                                >
                                                                    <ArrowUpRight size={14} />
                                                                </Link>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </AdminTable>
                                </div>
                            )}
                        </div>

                        <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500">
                                Total: {summaryEvents.length} reservas en este rango
                            </span>
                            <button
                                onClick={() => setIsSummaryModalOpen(false)}
                                className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-all"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
        </>
    );
};

export default ReservasCalendarioPage;

